import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Checkbox, Divider, FormControlLabel, MenuItem, Select } from '@mui/material';
import { RootState } from 'src/store/store';
import { getLabelFallback, range } from '@utils/utils';
import { IAddToCartNewPrescriptionPayload, IContactLensForm } from '@interfaces/contactsInterfaces';
import CLDiscounts from '../my-new-prescription/CLDiscounts';
import { cleanContactLensForm, getInitialValue } from '@utils/pages-utils/ContactsUtils';
import useOnFormSubmit from '@hooks/useOnFormSubmit';
import useOnFormChange from '@hooks/useOnFormChange';
import ArrowDown from '@images/svg-icons/arrowDown.svg';
import { useState } from 'react';
import InfoIconSmall from '@images/svg-icons/infoIconSmall.svg';

const menuOptions = {
    PaperProps: {
        style: {
            maxHeight: 220, // set a maximum height for the menu
        },
    },
};

const NewPrescriptionForm = (): JSX.Element => {
    const config = useSelector((state: RootState) => state.common.config);
    const currentProduct = useSelector((state: RootState) => state.contacts.currentProduct);

    const {
        power: powerValueOptions,
        baseCurve: bcValueOptions,
        diameter: diaValueOptions,
        cylinder: cylValueOptions,
        axis: axisValueOptions,
        add: addValueOptions,
        color: colorValueOptions,
        dominance: dominanceValueOptions,
    } = currentProduct?.attributes;

    const cachedCL = config.cachedCL;
    const [isODAvailable, setIsODAvailable] = useState(cachedCL?.OD ? true : false);
    const [isOSAvailable, setIsOSAvailable] = useState(cachedCL?.OS ? true : false);
    const modelOD = cachedCL?.OD?.product?.description[0]?.model;
    const modelOS = cachedCL?.OS?.product?.description[0]?.model;
    const boxQuantityOD = cachedCL?.OD?.product?.boxQuantity;
    const boxQuantityOS = cachedCL?.OS?.product?.boxQuantity;

    const boxesValueOptions = range(1, config.maxBoxes, true).map(String);
    let selectedBoxesValue = '';

    if (config.isExternalFeed) {
        selectedBoxesValue = '1';
    } else if (isODAvailable || isOSAvailable) {
        selectedBoxesValue = Math.max(1, config.discountData.sixMonthsSupplyBoxes).toString();
    } else {
        selectedBoxesValue = config.defaultBoxes.toString();
    }

    // TODP-12912 | unselected left eye when source=google
    const sourceGoogle = new URL(window.location.href).searchParams.get('source')?.toLowerCase() === 'google';

    const initialState: IContactLensForm = {
        rxLeftEye: config.isExternalFeed ? false : !sourceGoogle,
        leftQuantity: isOSAvailable ? boxQuantityOS.toString() : selectedBoxesValue,
        rxLeftAdd: getInitialValue(addValueOptions),
        rxLeftAxis: getInitialValue(axisValueOptions),
        rxLeftBc: getInitialValue(bcValueOptions),
        rxLeftColor: getInitialValue(colorValueOptions),
        rxLeftCyl: getInitialValue(cylValueOptions),
        rxLeftDia: getInitialValue(diaValueOptions),
        rxLeftPower: getInitialValue(powerValueOptions),
        rxLeftDominance: getInitialValue(dominanceValueOptions),
        rxRightEye: true,
        rightQuantity: isODAvailable ? boxQuantityOD.toString() : selectedBoxesValue,
        rxRightAdd: getInitialValue(addValueOptions),
        rxRightAxis: getInitialValue(axisValueOptions),
        rxRightBc: getInitialValue(bcValueOptions),
        rxRightColor: getInitialValue(colorValueOptions),
        rxRightCyl: getInitialValue(cylValueOptions),
        rxRightDia: getInitialValue(diaValueOptions),
        rxRightPower: getInitialValue(powerValueOptions),
        rxRightDominance: getInitialValue(dominanceValueOptions),
        samePrescription: false,
    };

    const schema = yup.object().shape({
        rxLeftEye: yup
            .boolean()
            .test(
                'invalid',
                'Please select at least one eye',
                (rxLeftEye, ctx) => rxLeftEye || ctx.parent.rxRightEye
            ),
        rxRightEye: yup
            .boolean()
            .test(
                'invalid',
                'Please select at least one eye',
                (rxRightEye, ctx) => rxRightEye || ctx.parent.rxLeftEye
            ),
        leftQuantity: yup
            .string()
            .test('invalid', 'Please enter at least 1 box', (val, ctx) =>
                ctx.parent.rxLeftEye ? parseInt(val) > 0 : true
            ),
        rightQuantity: yup
            .string()
            .test('invalid', 'Please enter at least 1 box', (val, ctx) =>
                ctx.parent.rxRightEye ? parseInt(val) > 0 : true
            ),
        rxLeftAdd: yup
            .string()
            .test('invalid', 'rxLeftAdd', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && addValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftAxis: yup
            .string()
            .test('invalid', 'rxLeftAxis', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && axisValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftBc: yup
            .string()
            .test('invalid', 'rxLeftBc', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && bcValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftColor: yup
            .string()
            .test('invalid', 'rxLeftColor', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && colorValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftCyl: yup
            .string()
            .test('invalid', 'rxLeftCyl', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && cylValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftDia: yup
            .string()
            .test('invalid', 'rxLeftDia', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && diaValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftPower: yup
            .string()
            .test('invalid', 'rxLeftPower', (val, ctx) =>
                ctx.parent.rxLeftEye && !ctx.parent.samePrescription && powerValueOptions?.length
                    ? !!val
                    : true
            ),
        rxLeftDominance: yup
            .string()
            .test('invalid', 'rxLeftDominance', (val, ctx) =>
                ctx.parent.rxLeftEye &&
                    !ctx.parent.samePrescription &&
                    dominanceValueOptions?.length
                    ? !!val
                    : true
            ),
        rxRightAdd: yup
            .string()
            .test('invalid', 'rxRightAdd', (val, ctx) =>
                ctx.parent.rxRightEye && addValueOptions?.length ? !!val : true
            ),
        rxRightAxis: yup
            .string()
            .test('invalid', 'rxRightAxis', (val, ctx) =>
                ctx.parent.rxRightEye && axisValueOptions?.length ? !!val : true
            ),
        rxRightBc: yup
            .string()
            .test('invalid', 'rxRightBc', (val, ctx) =>
                ctx.parent.rxRightEye && bcValueOptions?.length ? !!val : true
            ),
        rxRightColor: yup
            .string()
            .test('invalid', 'rxRightColor', (val, ctx) =>
                ctx.parent.rxRightEye && colorValueOptions?.length ? !!val : true
            ),
        rxRightCyl: yup
            .string()
            .test('invalid', 'rxRightCyl', (val, ctx) =>
                ctx.parent.rxRightEye && cylValueOptions?.length ? !!val : true
            ),
        rxRightDia: yup
            .string()
            .test('invalid', 'rxRightDia', (val, ctx) =>
                ctx.parent.rxRightEye && diaValueOptions?.length ? !!val : true
            ),
        rxRightPower: yup
            .string()
            .test('invalid', 'rxRightPower', (val, ctx) =>
                ctx.parent.rxRightEye && powerValueOptions?.length ? !!val : true
            ),
        rxRightDominance: yup
            .string()
            .test('invalid', 'rxRightDominance', (val, ctx) =>
                ctx.parent.rxRightEye && dominanceValueOptions?.length ? !!val : true
            ),
    });

    const methods = useForm<IContactLensForm>({
        resolver: yupResolver(schema),
        defaultValues: initialState,
    });

    const watchLeftEnabled = methods.watch('rxLeftEye');
    const watchRightEnabled = methods.watch('rxRightEye');
    const watchForm = useWatch({ control: methods.control }) as IContactLensForm;

    useOnFormChange({
        ...cleanContactLensForm(watchForm),
        leftIsCurrentProduct: watchLeftEnabled && !isOSAvailable,
        rightIsCurrentProduct: watchRightEnabled && !isODAvailable,
    });

    const onSubmit: SubmitHandler<IContactLensForm> = (data: IContactLensForm) => {
        const submitData: IAddToCartNewPrescriptionPayload = {
            isMockRequest: config.mockRequest,
            storeId: config.storeId,
            catalogId: config.catalogId,
            langId: -1,
            action: 'add',
            cache: false,
            errorViewName: 'LogonAjaxView',
            orderId: '.',
            partType: 'CONTACT_LENSES',
            URL: 'GetCatalogEntryDetailsByID',
            frame: config.productId,
            catEntryId: config.productId,
            autosetquantity: 'supplyAmount',
            existingOrderNumber: data.existingOrderNumber,
            ...cleanContactLensForm(data),
        };

        document.getElementById('prescription-error-cl').classList.add('hide');
        // delete submitData.isMockRequest; // remove for rest request

        // reverse mapping CL_DOMINANCE
        if (!!submitData.rxRightDominance) {
            submitData.rxRightDominance =
                submitData.rxRightDominance === 'D'
                    ? 'True'
                    : submitData.rxRightDominance === 'N'
                        ? 'False'
                        : '';
        }
        if (!!submitData.rxLeftDominance) {
            submitData.rxLeftDominance =
                submitData.rxLeftDominance === 'D'
                    ? 'True'
                    : submitData.rxLeftDominance === 'N'
                        ? 'False'
                        : '';
        }

        config.onFormSubmit(submitData, true);
        console.log('>>> New CL prescription form submit', submitData);
    };

    const onInvalid = error => {
        config.onFormSubmit(null, false);
        console.error('>>> New CL prescription form invalid', error);
    };

    useOnFormSubmit(methods, onSubmit, onInvalid);

    const { annualSupplyBoxes, sixMonthsSupplyBoxes, annualSupplySavings, sixMonthsSupplySavings, rebateAnnualSupplySavings, rebateSixMonthsSupplySavings } = config.discountData;
    const isNewGuestOffer = config.isNewGuestOffer;
    const isRebateAvailable = !!rebateAnnualSupplySavings || !!rebateSixMonthsSupplySavings;

    const setQuantity = (boxes: any) => {
        if (isODAvailable) {
            methods.setValue('leftQuantity', (boxes*2).toString());
            methods.setValue('rxLeftEye', true);
        } else if (isOSAvailable) {
            methods.setValue('rightQuantity', (boxes*2).toString());
            methods.setValue('rxRightEye', true);
        } else {
            methods.setValue('rightQuantity', boxes.toString());
            methods.setValue('rxRightEye', true);
            methods.setValue('leftQuantity', boxes.toString());
            methods.setValue('rxLeftEye', true);
        }
    };

    const getTotalBoxes = (): number => {
        const rightQuantity = watchRightEnabled ? Number(methods.getValues().rightQuantity) : 0;
        const leftQuantity = watchLeftEnabled ? Number(methods.getValues().leftQuantity) : 0;
        return rightQuantity + leftQuantity;
    };

    const getSavingsMessage = () => {
       const totalBoxes = getTotalBoxes();
        
        if (totalBoxes >= annualSupplyBoxes && annualSupplySavings != 0) {
            if (isNewGuestOffer)
                return "You're getting " + annualSupplySavings + " off your first order!";
            return "You're saving " + annualSupplySavings + " with an annual supply!";
        } else if (totalBoxes >= sixMonthsSupplyBoxes && sixMonthsSupplySavings != 0) {
            if (annualSupplyBoxes) {
                const boxesNeeded = annualSupplyBoxes - totalBoxes;

                if (isNewGuestOffer)
                    return (
                        <>
                            <div>You're getting {sixMonthsSupplySavings} off your first order!</div>
                            <div><button onClick={() => setQuantity(annualSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> to unlock {annualSupplySavings} off.</div>
                        </>
                    );
                return (
                    <>
                        <div>You're saving {sixMonthsSupplySavings} with a 6-month supply!</div>
                        <div><button onClick={() => setQuantity(annualSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> to save {annualSupplySavings}.</div>
                    </>
                );
            } else {
                if (isNewGuestOffer)
                    return "You're getting " + sixMonthsSupplySavings + " off your first order!";
                return "You're saving " + sixMonthsSupplySavings + " with a 6-month supply!";
            }
        } else {
            if (annualSupplyBoxes) {
                const boxesNeeded = annualSupplyBoxes - totalBoxes;

                if (totalBoxes && isNewGuestOffer && sixMonthsSupplySavings != 0) {
                    return (
                        <>
                            <div>You're getting {sixMonthsSupplySavings} off your first order!</div>
                            <div><button onClick={() => setQuantity(annualSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> to unlock {annualSupplySavings} off.</div>
                        </>
                    );
                }
                return (
                    <>
                        <button onClick={() => setQuantity(annualSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> and save {annualSupplySavings} on an annual supply.
                    </>
                );
            } else {
                const boxesNeeded = sixMonthsSupplyBoxes - totalBoxes;

                if (isNewGuestOffer && sixMonthsSupplySavings != 0) {
                    return (
                        <>
                            <button onClick={() => setQuantity(sixMonthsSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> to unlock {sixMonthsSupplySavings} off.
                        </>
                    );
                }
                return (
                    <>
                        <button onClick={() => setQuantity(sixMonthsSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> and save {sixMonthsSupplySavings} on a 6-month supply.
                    </>
                );
            }
        }
    };

    const getRebateSavingsMessage = () => {
        const totalBoxes = getTotalBoxes();
        const isAS = totalBoxes >= annualSupplyBoxes; 

        if (!isAS && totalBoxes < sixMonthsSupplyBoxes) return <></>;

        return <div className='rebates-message-container'>
            {!isAS && !rebateSixMonthsSupplySavings ? (<>
                {isNewGuestOffer ? <div>You're getting {sixMonthsSupplySavings} off your first order!</div> :<div>You're saving {sixMonthsSupplySavings} with a 6-month supply!</div>}
            </>) : (
                <>
                    <div>Save {isAS ? annualSupplySavings : sixMonthsSupplySavings} today!</div>
                    <div>Plus, up to  {isAS ? rebateAnnualSupplySavings : rebateSixMonthsSupplySavings} in manufacturer rebates if you qualify.</div>
                </>
            )}
        </div>
    };

    const getRebateMonthsSavingsMessage = () =>{
        const totalBoxes = getTotalBoxes();
        const isAS = totalBoxes >= annualSupplyBoxes; 
        const boxesNeeded = annualSupplyBoxes - totalBoxes;

        if (isAS) return <></>;

        return (
            <div className='savings-sub-container'>
                <div><button onClick={() => setQuantity(annualSupplyBoxes/2)}>Add {boxesNeeded} more boxes</button> to save {annualSupplySavings} today!</div>
                <div>Plus, up to {rebateAnnualSupplySavings} in manufacturer rebates if you qualify.</div>
            </div>
        );
    }

    const openCLRebateModal = (): void => {
        if (typeof openRebatesModal === 'function') {
            openRebatesModal();
        }
    };

    const getSavingsColor = () => {
        const totalBoxes = getTotalBoxes();

        if ((annualSupplyBoxes && totalBoxes >= annualSupplyBoxes)) return 'savings-blue';
        return 'savings-orange';
    };

    const handleRemoveProduct = (removedEye: string) => {
        config.onRemoveProduct(removedEye);
        if (removedEye == 'OD') {
            const currentRight = parseInt(methods.getValues('rightQuantity'));
            
            setIsODAvailable(false);
            if (currentRight > config.maxBoxes) {
                methods.setValue('rightQuantity', config.maxBoxes.toString());
            }
        } else if (removedEye == 'OS') {
            const currentLeft = parseInt(methods.getValues('leftQuantity'));

            setIsOSAvailable(false);
            if (currentLeft > config.maxBoxes) {
                methods.setValue('leftQuantity', config.maxBoxes.toString());
            }
        }
    };

    return (
        <FormProvider {...methods}>
            <CLDiscounts
                setQuantity={(boxes) => setQuantity(boxes)}
                quantityS={selectedBoxesValue}
                quantityB={{
                    leftQuantity: methods.getValues().leftQuantity,
                    rightQuantity: methods.getValues().rightQuantity,
                }}
                enabledEyes={{ rightEye: watchRightEnabled, leftEye: watchLeftEnabled }}
            />
            <form
                id={config.prescriptionFormId + 'new'}
                className="contact-lens-prescription-content"
                onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
            >
                <div className="form-container">
                    <div className={`eye-container ${isODAvailable || isOSAvailable ? 'multi-product' : ''}`}>
                        <div className={`eye-row ${isODAvailable ? 'multi-product' : ''}`}>
                            <div className="eye-checkbox-container">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...methods.register('rxRightEye')}
                                            size="small"
                                            inputProps={{
                                                'aria-label': 'Check OD (right)',
                                            }}
                                            defaultChecked
                                            checked={watchRightEnabled || isODAvailable}
                                            disabled={isODAvailable}
                                        />
                                    }
                                    label={getLabelFallback('CONTACT_LENSES_RIGHT_EYE')}
                                />
                            </div>
                            {
                                !isODAvailable ? (
                                    <div className="presc-param-select boxes-select right">
                                        <FormControlLabel
                                            control={
                                                <Controller
                                                    name="rightQuantity"
                                                    control={methods.control}
                                                    defaultValue=""
                                                    disabled={isODAvailable}
                                                    render={({ field: { onChange, value } }) => (
                                                        <Select
                                                            aria-label="Right eye boxes"
                                                            disabled={!watchRightEnabled || isODAvailable}
                                                            value={value}
                                                            onChange={onChange}
                                                            defaultValue={selectedBoxesValue || ''}
                                                            className="param-select"
                                                            IconComponent={props => <ArrowDown {...props} />}
                                                            MenuProps={menuOptions}
                                                            id="rightQuantity">
                                                            {boxesValueOptions.map((v: string | number) => (
                                                                <MenuItem key={v} value={v}>
                                                                    {v}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            }
                                            label={getLabelFallback('CONTACT_LENSES_QTY')}
                                        />
                                    </div>
                                ) : (
                                    <div className="multi-product-container">
                                        <div className="multi-product-text-container">
                                            <div className="multi-product-model-copy">{modelOD}</div>
                                            <button className="multi-product-remove-cta" onClick={() => handleRemoveProduct('OD')}>{getLabelFallback('CONTACT_LENSES_REMOVE')}</button>
                                        </div>
                                        <div className="presc-param-select boxes-select right">
                                            <FormControlLabel
                                                control={
                                                    <Controller
                                                        name="rightQuantity"
                                                        control={methods.control}
                                                        defaultValue=""
                                                        disabled={isODAvailable}
                                                        render={({ field: { onChange, value } }) => (
                                                            <Select
                                                                aria-label="Right eye boxes"
                                                                disabled={!watchRightEnabled || isODAvailable}
                                                                value={value}
                                                                onChange={onChange}
                                                                defaultValue={selectedBoxesValue || ''}
                                                                className="param-select"
                                                                IconComponent={props => <ArrowDown {...props} />}
                                                                MenuProps={menuOptions}
                                                                id="rightQuantity">
                                                                {boxesValueOptions.map((v: string | number) => (
                                                                    <MenuItem key={v} value={v}>
                                                                        {v}
                                                                    </MenuItem>
                                                                ))}
                                                                {value && !boxesValueOptions.includes(value) && (
                                                                    <MenuItem key={value} value={value} style={{ display: 'none' }}>
                                                                        {value}
                                                                    </MenuItem>
                                                                )}
                                                            </Select>
                                                        )}
                                                    />
                                                }
                                                label={getLabelFallback('CONTACT_LENSES_QTY')}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                        {(isODAvailable || isOSAvailable) && <Divider orientation="horizontal" flexItem />}
                        <div className={`eye-row ${isOSAvailable ? 'multi-product' : ''}`}>
                            <div className="eye-checkbox-container">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...methods.register('rxLeftEye')}
                                            size="small"
                                            inputProps={{
                                                'aria-label': 'Check OS (left)',
                                            }}
                                            checked={watchLeftEnabled || isOSAvailable}
                                            disabled={isOSAvailable}
                                        />
                                    }
                                    label={getLabelFallback('CONTACT_LENSES_LEFT_EYE')}
                                />
                            </div>
                            {
                                !isOSAvailable ? (
                                    <div className="presc-param-select boxes-select left">
                                        <FormControlLabel
                                            control={
                                                <Controller
                                                    name="leftQuantity"
                                                    control={methods.control}
                                                    defaultValue=""
                                                    disabled={isOSAvailable}
                                                    render={({ field: { onChange, value } }) => (
                                                        <Select
                                                            aria-label="Left eye boxes"
                                                            disabled={!watchLeftEnabled || isOSAvailable}
                                                            value={value}
                                                            onChange={onChange}
                                                            defaultValue={selectedBoxesValue || ''}
                                                            className="param-select"
                                                            IconComponent={props => <ArrowDown {...props} />}
                                                            MenuProps={menuOptions}
                                                            id="leftQuantity">
                                                            {boxesValueOptions.map((v: string | number) => (
                                                                <MenuItem key={v} value={v}>
                                                                    {v}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            }
                                            label={getLabelFallback('CONTACT_LENSES_QTY')}
                                        />
                                    </div>
                                ) : (
                                    <div className="multi-product-container">
                                        <div className="multi-product-text-container">
                                            <div className="multi-product-model-copy">{modelOS}</div>
                                            <button className="multi-product-remove-cta" onClick={() => handleRemoveProduct('OS')}>{getLabelFallback('CONTACT_LENSES_REMOVE')}</button>
                                        </div>
                                        <div className="presc-param-select boxes-select left">
                                            <FormControlLabel
                                                control={
                                                    <Controller
                                                        name="leftQuantity"
                                                        control={methods.control}
                                                        defaultValue=""
                                                        disabled={isOSAvailable}
                                                        render={({ field: { onChange, value } }) => (
                                                            <Select
                                                                aria-label="Left eye boxes"
                                                                disabled={!watchLeftEnabled || isOSAvailable}
                                                                value={value}
                                                                onChange={onChange}
                                                                defaultValue={selectedBoxesValue || ''}
                                                                className="param-select"
                                                                IconComponent={props => <ArrowDown {...props} />}
                                                                MenuProps={menuOptions}
                                                                id="leftQuantity">
                                                                {boxesValueOptions.map((v: string | number) => (
                                                                    <MenuItem key={v} value={v}>
                                                                        {v}
                                                                    </MenuItem>
                                                                ))}
                                                                {value && !boxesValueOptions.includes(value) && (
                                                                    <MenuItem key={value} value={value} style={{ display: 'none' }}>
                                                                        {value}
                                                                    </MenuItem>
                                                                )}
                                                            </Select>
                                                        )}
                                                    />
                                                }
                                                label={getLabelFallback('CONTACT_LENSES_QTY')}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </form>
            {(isRebateAvailable || annualSupplySavings != 0 || sixMonthsSupplySavings != 0) &&
                <div className='supply-info-container'>
                    {(annualSupplySavings != 0 || sixMonthsSupplySavings != 0) && (<>

                        <div className={`savings-container ${getSavingsColor()}`}>
                            {isRebateAvailable ? getRebateSavingsMessage() : getSavingsMessage()}
                        </div>
                        {isRebateAvailable && getRebateMonthsSavingsMessage()}
                    </>)
                    }

                    {isRebateAvailable && 
                        <button className='rebate-info' onClick={openCLRebateModal}>
                            <InfoIconSmall /><span>{getLabelFallback('REBATE_INFO')}</span>
                        </button>
                    }
                </div>
            }
        </FormProvider>
    );
};

export default NewPrescriptionForm;
